/**
 * Dismiss a NEEDS ATTENTION open-inbound row (Bug 35644, Tim: "false alarms
 * and no way to acknowledge them"). Dismiss posts `dismissed: true` to the
 * bridge's open-inbound resolve route, which closes the chat's rows and
 * records the caller as the dismisser. The row hides on this screen at once;
 * every screen drops it on the bridge's openInboundChanged push. The hide is
 * keyed by chat AND arrival time, so a new message on the same chat shows
 * again. A failed write brings the row back and says so on the gaps line.
 */
import { computed, signal } from '@angular/core';
import type { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type { Alert } from './attention.logic';

export const DISMISS_GAP_KEY = 'Dismiss';
export const DISMISS_WRITE_FAILED =
  'a dismissed row could not be closed on the bridge, it is back';

/** chatId to the newest inbound arrival (ms) dismissed on that chat. */
export type InboundDismissals = ReadonlyMap<string, number>;

/** The alerts to show: rows whose inbound was dismissed here dropped. */
export function hideDismissed(
  alerts: readonly Alert[],
  dismissed: InboundDismissals
): Alert[] {
  return alerts.filter((a) => {
    if (!a.dismiss) return true;
    const upTo = dismissed.get(a.dismiss.chatId);
    return upTo === undefined || a.dismiss.receivedAtMs > upTo;
  });
}

export class InboundDismissSync {
  readonly dismissed = signal<InboundDismissals>(new Map());
  readonly error = computed(() => this.writeError());
  private readonly writeError = signal<string | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly baseUrl: () => string
  ) {}

  visible(alerts: readonly Alert[]): Alert[] {
    return hideDismissed(alerts, this.dismissed());
  }

  /** Source errors plus a failed dismiss, for the gaps lines. */
  withError(
    errors: Readonly<Partial<Record<string, string>>>
  ): Readonly<Partial<Record<string, string>>> {
    const e = this.error();
    return e ? { ...errors, [DISMISS_GAP_KEY]: e } : errors;
  }

  async dismiss(target: {
    chatId: string;
    receivedAtMs: number;
  }): Promise<void> {
    const before = this.dismissed();
    const next = new Map(before);
    next.set(
      target.chatId,
      Math.max(before.get(target.chatId) ?? 0, target.receivedAtMs)
    );
    this.dismissed.set(next);
    try {
      await firstValueFrom(
        this.http.post(`${this.baseUrl()}/ryn-db/open-inbound/resolve`, {
          chatId: target.chatId,
          dismissed: true,
        })
      );
      this.writeError.set(null);
    } catch {
      const undo = new Map(this.dismissed());
      const prior = before.get(target.chatId);
      if (prior === undefined) undo.delete(target.chatId);
      else undo.set(target.chatId, prior);
      this.dismissed.set(undo);
      this.writeError.set(DISMISS_WRITE_FAILED);
    }
  }
}
